import { useMemo } from 'react';
import { Country, Status } from '@tiendanube/common/src/enums';
import { InterfaceLabel } from '@tiendanube/components';
import { useNavegate } from 'App/hooks';
import { useResponsive } from 'commons/components';
import useModal from 'commons/hooks/useModal';
import goToAdmin from 'commons/utils/gotToAdmin/goToAdmin';
import {
  useGetCountry,
  useHasPermission,
  useIsMobileDevice,
} from 'domains/Auth/hooks';
import { OrderMarkAsPaidModal } from 'domains/Orders/components/OrderMarkAsPaidModal';
import { getLatestPayment } from 'domains/Orders/Orders/utils/OrderDetailsPage/utils';
import {
  trackingOrderMarkAsPaidClick,
  trackingTransactionLinkClick,
} from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders/useTranslationOrders';
import NewCardPayment from './NewCardPayment';
import useNuvemPagoRedirect from '../../../hooks/useNuvemPagoRedirect/useNuvemPagoRedirect';
import { payment as getPaymentStatusObject } from '../commons/Status/status';
import { OrderStatusType } from '../commons/useOrderStatus';

interface CardPaymentProps {
  order: OrderStatusType;
  isMarkingAsPaid: boolean;
  paidOrder: (
    gateway?: string,
    gatewayMethod?: string,
    gatewayName?: string,
  ) => void;
  partiallyPaidOrder: (method: string, name?: string) => void;
  isMakingPartialPayment: boolean;
}

function CardPayment({
  order,
  isMarkingAsPaid,
  paidOrder,
  partiallyPaidOrder,
  isMakingPartialPayment,
}: Readonly<CardPaymentProps>): JSX.Element {
  const orderStatus = order.status;
  const payment = getLatestPayment(order);
  //NOTE: For now we are assuming multiple payment methods for the same gateway provider.
  const gateway = payment.gateway ?? '';
  const nuvemPagoRedirect = useNuvemPagoRedirect(gateway);

  const t = useTranslationOrders();
  const isMobile = useIsMobileDevice();
  const { goTo } = useNavegate();
  const country = useGetCountry();
  const { isDesktop } = useResponsive();
  const canSetPaymentReceived = useHasPermission('set_payment_received');
  const [showConfirmationModal, openConfirmationModal, closeConfirmationModal] =
    useModal();
  const isArchived = orderStatus === Status.ARCHIVED;
  const isCancelled = orderStatus === Status.CANCELLED;

  const openGatewayLink = (gatewayLink: string) => {
    trackingTransactionLinkClick();
    window.open(gatewayLink, '_blank');
  };

  const openNuvemPago = () => {
    if (country === Country.BR) goToAdmin('/nuvempago/dashboard')();
    else goTo('/apps/nuvempago');
  };

  const onMarkAsPaidConfirm = () => {
    closeConfirmationModal();
    paidOrder();
    trackingOrderMarkAsPaidClick();
  };

  const headerLabel = useMemo<InterfaceLabel>(() => {
    const paymentLabel = getPaymentStatusObject(payment.status);

    return {
      ...paymentLabel,
      id: payment.status,
      label: `${t(`status.${paymentLabel.status}`)}`,
    };
  }, [t, payment.status]);

  const showButton =
    ![Status.PAID, Status.REFUNDED, Status.PARTIALLY_REFUNDED].includes(
      payment.status,
    ) && canSetPaymentReceived;

  return (
    <>
      <NewCardPayment
        order={order}
        t={t}
        showButton={showButton}
        headerLabel={headerLabel}
        nuvemPagoRedirect={nuvemPagoRedirect}
        openGatewayLink={openGatewayLink}
        country={country}
        isMobile={isMobile}
        isDesktop={isDesktop}
        openNuvemPago={openNuvemPago}
        isArchived={isArchived}
        isCancelled={isCancelled}
        isMarkingAsPaid={isMarkingAsPaid}
        paidOrder={paidOrder}
        partiallyPaidOrder={partiallyPaidOrder}
        isMakingPartialPayment={isMakingPartialPayment}
        handleMarkAsPaid={openConfirmationModal}
      />
      {showConfirmationModal && (
        <OrderMarkAsPaidModal
          orderId={order.number}
          total={order.amounts.total}
          onClose={closeConfirmationModal}
          onConfirm={onMarkAsPaidConfirm}
          show={showConfirmationModal}
        />
      )}
    </>
  );
}

export default CardPayment;
